import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import type { FilterEntry } from "./filters";

export const defaultSort: Array<SortEntry> = [
  {
    property: "grade",
    direction: "asc",
  },
];

export const defaultColorSort: Array<SortEntry> = [
  {
    property: "deltaE",
    direction: "asc",
  },
];

export interface SortEntry {
  property: string;
  direction: "asc" | "desc";
}

/**
 * Sort used when the user has not picked one. Searching by an OEM colour code / name
 * ranks the results by their distance to that colour (CX-4172).
 */
export function resolveDefaultSort(
  filters: Array<FilterEntry>,
): Array<SortEntry> {
  const isOemColorSearch = filters.some(
    (filter) =>
      filter.type === "color" &&
      filter.value?.colorSystem === "oem_color_code" &&
      filter.value.color !== undefined,
  );
  return isOemColorSearch ? defaultColorSort : defaultSort;
}

/** Sorts a result list by one table column; unknown properties keep the order. */
export function sortColoredMaterials({
  values,
  property,
  direction,
}: {
  values: Array<ColoredMaterial>;
  property: string;
  direction: SortEntry["direction"];
}): Array<ColoredMaterial> {
  const sign = direction === "asc" ? 1 : -1;
  const sorted = [...values];
  if (property === "grade") {
    sorted.sort(
      (a, b) =>
        sign * (a.material.grade || "").localeCompare(b.material.grade || ""),
    );
  } else if (property === "finalColorCode") {
    sorted.sort(
      (a, b) =>
        sign *
        (a.color.finalColorCode || "").localeCompare(
          b.color.finalColorCode || "",
          undefined,
          { numeric: true },
        ),
    );
  } else if (property === "deltaE") {
    sorted.sort(
      (a, b) =>
        sign *
        ((a.color.specifications?.deltaE || 0) -
          (b.color.specifications?.deltaE || 0)),
    );
  } else if (property === "color") {
    sorted.sort(
      (a, b) =>
        sign *
        ((a.color.specifications?.colorSortIndex || 0) -
          (b.color.specifications?.colorSortIndex || 0)),
    );
  }
  return sorted;
}

export function urlToSort(params: URLSearchParams): Array<SortEntry> {
  return params.getAll("sort").map((value) => ({
    property: value.startsWith("-") ? value.substring(1) : value,
    direction: value.startsWith("-") ? "desc" : "asc",
  }));
}

export function sortToUrl(
  sort: Array<SortEntry>,
): Record<string, string | string[]> {
  return {
    sort: sort.map(
      (value) => (value.direction === "asc" ? "" : "-") + value.property,
    ),
  };
}
